import {useEffect, useState} from "react";
import ApiDatabase from "server";
import usefetchdata from "../../../../../hooks/usefetchdata";
import Divider from "../../../../divider/divider";
import Button from "../../../../button";
import Badge from "../../../../badge/badge";
import Tooltip from "../../../../tooltips/Tooltips";
import {NoteIcon, TrashIcon, Pencil} from "../../../../../assets/icons";
import {dateWordMonthShortHour, generateEmployeeColor} from "../../../../../helper/helper"
import UseContext from "../../../../../hooks/useContext";
import useModal from "../../../../modal/useModal";

const CommentTempWorker = ({idUser}) => {
    const token = localStorage.getItem('xsrfToken')
    const {userData} = usefetchdata()
    const {setCommentID, commentIDUpd} = UseContext()
    const {toggle} = useModal()
    const [comments, setComments] = useState([])
    const [isWaiting, setIsWaiting] = useState(true)
    const [commentText, setCommentText] = useState('')
    const [editingCommentId, setEditingCommentId] = useState(null)

    useEffect(() => {
        if (token && idUser && userData._id) {
            setIsWaiting(true)
            ApiDatabase.getCommentsByTempWorker({token, idUser}, (data) => {
                setComments(data)
                setIsWaiting(false)
            }, (err) => {
                console.log(err)
            })
        }
    }, [token, userData._id, idUser]);

    const addComment = () => {
        if (commentText.length >= 5) {
            if (editingCommentId) {
                // Mode édition
                ApiDatabase.postCommentTempWorker(
                    {token, idUser, idComment: editingCommentId, comment: commentText}, 
                    (data) => {
                        setComments(prevData => prevData.map(comment => 
                            comment._id === editingCommentId ? data : comment
                        ))
                        setCommentText('')
                        setEditingCommentId(null)
                    }, 
                    (err) => {
                        console.log(err)
                    }
                )
            } else {
                // Mode ajout
                ApiDatabase.postCommentTempWorker({token, idUser, comment: commentText}, (data) => {
                    setComments(prevData => ([...prevData, data]))
                    setCommentText('')
                }, (err) => {
                    console.log(err)
                })
            }
        }
    }

    const deleteComment = (idComment) => {
        if (idComment) {
            setCommentID(idComment)
            toggle('user_delete_comment')
        }
    }

    const startEditing = (comment) => {
        setEditingCommentId(comment._id)
        setCommentText(comment.comment)
        // Scroll vers le bas pour voir le textarea
        setTimeout(() => {
            const textarea = document.querySelector('textarea')
            if (textarea) {
                textarea.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                textarea.focus()
            }
        }, 100)
    }

    const cancelEditing = () => {
        setEditingCommentId(null)
        setCommentText('')
    }

    useEffect(() => {
        if (commentIDUpd) {
            const updatedComments = comments.filter(comment => comment._id !== commentIDUpd);
            setComments(updatedComments)
        }
    }, [commentIDUpd]);

    return (
        <div className={'flex flex-col h-full'}>
            <div className={'flex-1 overflow-y-auto min-h-0 pb-4'}>
                {isWaiting ? (
                    <div className={'animate-pulse flex flex-col gap-4'}>
                        {[1, 2, 3, 4, 5].map((i, index) => (
                            <div key={index} className={'border border-gray-200 rounded-lg p-4 bg-white'}>
                                <div className={'flex justify-between items-start gap-4'}>
                                    <div className={'flex flex-col gap-3 flex-1'}>
                                        <div className={'flex gap-2 items-center'}>
                                            <div className={'w-5 h-5 bg-gray-200 rounded'}></div>
                                            <div className={'w-24 h-5 bg-gray-200 rounded'}></div>
                                        </div>
                                        <div className={'flex flex-col gap-2'}>
                                            <div className="w-full h-3 bg-gray-200 rounded"></div>
                                            <div className="w-4/5 h-3 bg-gray-200 rounded"></div>
                                        </div>
                                        <div className={'flex gap-2 items-center'}>
                                            <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
                                            <div className="w-32 h-3 bg-gray-200 rounded"></div>
                                        </div>
                                    </div>
                                    <div className={'w-8 h-8 bg-gray-200 rounded'}></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : comments?.length === 0 ? (
                    <div className={'flex flex-col items-center justify-center py-16 px-4'}>
                        <div className={'w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4'}>
                            <NoteIcon wh={32} color={'#9CA3AF'} />
                        </div>
                        <div className={'text-gray-900 font-semibold text-lg mb-2'}>Aucun commentaire</div>
                        <div className={'text-gray-500 text-sm text-center max-w-md'}>
                            Aucun commentaire n'a encore été ajouté pour cet intérimaire.
                        </div>
                    </div>
                ) : (
                    <div className={'flex flex-col gap-4'}>
                        {comments?.map((comment, index) => (
                            <div key={index} className={'border border-gray-200 rounded-lg p-4 bg-white hover:shadow-sm transition-shadow'}>
                                <div className={'flex justify-between items-start gap-4'}>
                                    <div className={'flex flex-col gap-3 flex-1 min-w-0'}>
                                        <div className={'flex gap-2 items-center'}>
                                            <div className={'flex items-center gap-2'}>
                                                <Badge type={comment.type === 'public' ? 'PRIMARY' : 'SECONDARY'} size={'SM'}>
                                                    {comment.type === 'public' ? 'Note publique' : 'Note privée'}
                                                </Badge>
                                            </div>
                                        </div>
                                        <div className={'text-gray-900 text-sm leading-relaxed'}>
                                            {comment.comment}
                                        </div>
                                        <div className={'text-gray-500 text-xs flex items-center gap-2'}>
                                            {comment.employee && (
                                                <div
                                                    className="rounded-full w-6 h-6 flex items-center justify-center text-[10px] text-white flex-shrink-0"
                                                    style={{
                                                        backgroundColor: generateEmployeeColor(
                                                            comment.employee.firstname,
                                                            comment.employee.lastname
                                                        ),
                                                    }}
                                                >
                                                    {comment.employee.firstname?.charAt(0).toUpperCase() || ''}
                                                    {comment.employee.lastname?.charAt(0).toUpperCase() || ''}
                                                </div>
                                            )}
                                            <span>{comment.employee?.firstname || 'NaN'} {comment.employee?.lastname || 'NaN'}</span>
                                            <span>·</span>
                                            <span>{dateWordMonthShortHour(comment.updatedAt)}</span>
                                        </div>
                                    </div>
                                    <div className={'flex-shrink-0 flex gap-1'}>
                                        {comment.employee?._id === userData?._id && (
                                            <Tooltip text="Modifier" placement="top">
                                                <div>
                                                    <Button 
                                                        size={'TABLE'} 
                                                        color={'TABLE'} 
                                                        onClick={() => startEditing(comment)}
                                                        className="hover:shadow-none hover:!bg-gray-100"
                                                    >
                                                        <Pencil wh={20} color={'#374151'}/>
                                                    </Button>
                                                </div>
                                            </Tooltip>
                                        )}
                                        {comment.employee?._id === userData?._id && (
                                            <Tooltip text="Supprimer" placement="top">
                                                <div>
                                                    <Button 
                                                        size={'TABLE'} 
                                                        color={'TABLE'} 
                                                        onClick={() => deleteComment(comment._id)}
                                                        className="hover:shadow-none hover:!bg-gray-100"
                                                    >
                                                        <TrashIcon wh={20} color={'#374151'}/>
                                                    </Button>
                                                </div>
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className={'flex flex-col flex-none gap-0 bg-white border-t border-gray-100'}>
                <div className={'border border-gray-100 rounded-lg flex flex-col'}>
                    <textarea 
                        cols="30"
                        rows="4"
                        className="w-full px-3 py-2.5 border-0 rounded-t-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                        placeholder="Écrivez votre note ici..."
                        required
                        minLength={5}
                        maxLength={500}
                        wrap="true"
                        value={commentText}
                        onChange={e => setCommentText(e.target.value)}
                    />
                    <div className={'flex justify-between items-center px-3 py-2.5 border-t border-gray-100'}>
                        <div className={'text-xs text-gray-500'}>
                            {commentText.length > 0 && commentText.length < 5 ? (
                                <span className={'text-red-500'}>5 caractères minimum</span>
                            ) : (
                                <span>{commentText.length} / 500 caractères</span>
                            )}
                        </div>
                        <div className={'flex gap-2'}>
                            {editingCommentId && (
                                <Button 
                                    color={'SECONDARY'} 
                                    size={'LG'} 
                                    onClick={cancelEditing}
                                >
                                    Annuler
                                </Button>
                            )}
                            <Button 
                                color={commentText.length < 5 ? 'DISABLED' : 'PRIMARY'} 
                                size={'LG'} 
                                onClick={() => addComment()}
                                disabled={commentText.length < 5}
                            >
                                {editingCommentId ? 'Enregistrer' : 'Ajouter'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CommentTempWorker